// frontend/user/src/components/file-icon.tsx
// 共用文件类型 icon — 给 file-browser.tsx(bi8Au)和 folder-detail.tsx(tC8FY)用。
//
// 2026-05-29 重写:改用 lucide-react,按 ui/dogdisk-user.pen bi8Au 4 item 设计稿配色:
//   - folder      (item1/item2):Folder icon,蓝 bg + 深蓝 icon
//   - video       (item3 vlog-旅行.mp4):Play icon,bg #FECACA + icon #991B1B
//   - image       (item4 sunset.jpg):Image icon,bg #FFE5B4 + icon #92400E
//   - audio       (设计稿未画;按 Lucide 紫色组合):Music icon,bg #E9D5FF + icon #6B21A8
//   - pdf         (旧):FileText 红
//   - doc/document(旧):FileText 绿
//   - 其他        (其他文件):File icon,bg #E5E7EB + icon #374151
//
// type 优先 (folder / image / video / audio / pdf / doc / document / 任意);
// 若 type 是 'file' / 'document' / 空 / 未识别,会按 mimeType 和文件名后缀降级匹配。

import type { ComponentType } from 'react'
import { File, FileText, Folder, Image as ImageIcon, Music, Play } from 'lucide-react'

export type FileKind = 'folder' | 'image' | 'video' | 'audio' | 'pdf' | 'doc' | 'other'

type LucideIcon = ComponentType<{ size?: number; strokeWidth?: number }>

const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'avif'])
const VIDEO_EXT = new Set(['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v', 'flv', 'wmv'])
const AUDIO_EXT = new Set(['mp3', 'm4a', 'wav', 'flac', 'ogg', 'aac', 'wma', 'opus'])
const DOC_EXT = new Set(['doc', 'docx', 'odt', 'rtf', 'txt', 'md', 'pages'])

function extOf(name: string | null | undefined): string {
  if (!name) return ''
  const i = name.lastIndexOf('.')
  return i < 0 ? '' : name.slice(i + 1).toLowerCase()
}

/** 把 type / mimeType / 文件名后缀 归一为 FileKind */
export function fileKind(
  type: string | null | undefined,
  opts: { mimeType?: string | null | undefined; name?: string | null | undefined } = {},
): FileKind {
  const t = (type ?? '').toLowerCase()
  if (t === 'folder') return 'folder'
  if (t === 'image') return 'image'
  if (t === 'video') return 'video'
  if (t === 'audio') return 'audio'
  if (t === 'pdf') return 'pdf'
  if (t === 'doc' || t === 'document') return 'doc'

  const mime = (opts.mimeType ?? '').toLowerCase()
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime === 'application/pdf') return 'pdf'

  const ext = extOf(opts.name)
  if (ext === 'pdf') return 'pdf'
  if (IMAGE_EXT.has(ext)) return 'image'
  if (VIDEO_EXT.has(ext)) return 'video'
  if (AUDIO_EXT.has(ext)) return 'audio'
  if (DOC_EXT.has(ext)) return 'doc'

  return 'other'
}

type Palette = {
  bg: string
  fg: string
  Icon: LucideIcon
}

// 配色来自 ui/dogdisk-user.pen bi8Au item2/item3/item4 + 项目既有 brand 蓝色保留
const PALETTE: Record<FileKind, Palette> = {
  folder: { bg: '#E8F1FF', fg: '#1971ca', Icon: Folder },
  video: { bg: '#FECACA', fg: '#991B1B', Icon: Play },
  image: { bg: '#FFE5B4', fg: '#92400E', Icon: ImageIcon },
  audio: { bg: '#E9D5FF', fg: '#6B21A8', Icon: Music },
  pdf: { bg: '#FFE4E4', fg: '#FF6B6B', Icon: FileText },
  doc: { bg: '#E5F8E8', fg: '#19B665', Icon: FileText },
  other: { bg: '#E5E7EB', fg: '#374151', Icon: File },
}

export type FileIconProps = {
  /** 主类型,优先级最高。可传 'folder' / 'image' / 'video' / 'audio' / 'pdf' / 'doc' / 其他 */
  type: string | null | undefined
  /** 兜底:mime_type 推断 */
  mimeType?: string | null | undefined
  /** 兜底:文件名后缀推断 */
  name?: string | null | undefined
  /** size 配色容器边长(px,默认 44 — 与 pen bi8Au thumb 一致) */
  size?: number
  /** 内 icon 边长(px,默认 20) */
  iconSize?: number
}

export function FileIcon({ type, mimeType, name, size = 44, iconSize = 20 }: FileIconProps) {
  const kind = fileKind(type, { mimeType, name })
  const { bg, fg, Icon } = PALETTE[kind]
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-lg"
      style={{ width: size, height: size, background: bg, color: fg }}
      data-file-kind={kind}
      aria-hidden="true"
    >
      <Icon size={iconSize} strokeWidth={1.8} />
    </span>
  )
}
